/**
 * Warstwa API modułu: I-c — offboarding.
 * Właściciel: FALA 1, agent I-c-1 (I-c-2..5 dopisują swoje) (docs/PROJEKT-00-FUNDAMENT-2026-09-02.md §5, §6).
 * Trasy backendu: api/offboarding.
 *
 * Kontrakt: `attach(api)` dostaje instancję axios z apiService.js (interceptory JWT, cache GET, obsługa 401)
 * i zwraca OBIEKT nazwanych metod, które apiService.js doczepia do wspólnego `apiService` (jedno miejsce,
 * src/api/modules/index.js). Nazwy metod muszą być unikalne w całej aplikacji — kolizja zatrzymuje start
 * (patrz attachApiModules). Tego pliku nie dotyka nikt poza właścicielem modułu.
 *
 * UWAGA: pozycji checklisty NIE odznacza się stąd — robi to warstwa `hrProcess.js`
 * (api/hr-process/tasks/{id}/…), bo o prawie do odhaczenia rozstrzyga JEDNA bramka serwera dla wszystkich
 * czterech wejść (kadry, przełożony w „Zadaniach", agent w karcie zgłoszenia, zakładka IT).
 */
export function attach(api) {
    return {
        // ── Lista i szczegół sprawy (/admin/offboarding) ────────────────
        getOffboardingCases: (params = {}) => api.get("/offboarding/cases", { params }),
        getOffboardingCase: (caseId) => api.get(`/offboarding/cases/${caseId}`),
        getOffboardingStats: () => api.get("/offboarding/stats"),

        /**
         * Sprawa danej OSOBY — panel „Ustanie zatrudnienia" na karcie akt.
         * Odpowiedź 204 (pusta) znaczy „brak sprawy albo brak zakresu" — panel pokazuje wtedy stan pusty,
         * a nie komunikat o błędzie.
         */
        getOffboardingByEmployee: (employeeId, open = true) =>
            api.get(`/offboarding/cases/by-employee/${employeeId}`, { params: { open } }),

        // ── Szuflada startu ─────────────────────────────────────────────
        getOffboardingPreview: (employeeId) => api.get(`/offboarding/preview/${employeeId}`),
        getOffboardingSuccessorHint: (employeeId) => api.get(`/offboarding/successor-suggestion/${employeeId}`),

        // ── Zapis ───────────────────────────────────────────────────────
        createOffboardingCase: (data) => api.post("/offboarding/cases", data),
        updateOffboardingCase: (caseId, data) => api.put(`/offboarding/cases/${caseId}`, data),
        refreshOffboardingTasks: (caseId) => api.post(`/offboarding/cases/${caseId}/refresh-tasks`, {}),

        /**
         * Zgłoszenie do IT z pozycjami technicznymi jako podzadaniami. Zwykle powstaje samo przy
         * zakładaniu sprawy — ta metoda jest drogą wyjścia, gdy tamto się nie udało albo gdy poprzednie
         * zgłoszenie zamknięto. Otwarte zgłoszenie = 409 `OFFBOARDING_IT_TICKET_OPEN` z jego numerem.
         */
        createOffboardingItTicket: (caseId) => api.post(`/offboarding/cases/${caseId}/it-ticket`, {}),

        /**
         * Zamknięcie sprawy. `blockAccount: true` = „Zakończ i zablokuj"; odpowiedź 409
         * `CIAGLOSC_STRUKTURY` niesie w polu `ciaglosc` komplet danych okna przepięcia podwładnych,
         * a ponowienie z `potwierdzamOsierocenie: true` jest ŚWIADOMĄ zgodą (nazwiska idą do dziennika).
         */
        closeOffboardingCase: (caseId, data = {}) => api.post(`/offboarding/cases/${caseId}/close`, data),
        cancelOffboardingCase: (caseId, data) => api.post(`/offboarding/cases/${caseId}/cancel`, data),

        // ── Ustawienia zakładki ─────────────────────────────────────────
        getOffboardingSettings: () => api.get("/offboarding/settings"),
        updateOffboardingSettings: (data) => api.put("/offboarding/settings", data),

        // ── Dokumenty odejścia (etap 4) ─────────────────────────────────
        //
        // ⚠️ ŻADNA z tych metod nie pobiera PLIKU. Wydany dokument otwiera się przez
        // `getDocumentIssuePdf(id)` z warstwy `documentIssues` — jedno źródło bajtów w całej
        // aplikacji (fundament Z-A). Tutaj powstaje wydanie i wraca jego identyfikator.

        /** Wzory dokumentów sprawy + wydania + wynik sondy silnika PDF. */
        getOffboardingDocuments: (caseId) => api.get(`/offboarding/cases/${caseId}/documents`),

        /**
         * ZAPISANY PLIK wydania — jedyne wejście po bajty, wskazuje wspólny
         * `GET api/document-issues/{id}/pdf` (fundament Z-A: jedna bramka, jeden dziennik otwarć,
         * jedna kontrola hasha). To nie jest drugi kanał, tylko cienka nazwa na ten sam adres.
         *
         * ⚠️ Świadomie własna metoda zamiast `getDocumentIssuePdf` z warstwy dokumentów działu:
         * tamta adresuje `"/api/document-issues/…"`, a `baseURL` axiosa już kończy się na `/api`
         * (.env: `REACT_APP_API_URL=…/api`) — czyli wychodzi z niej `/api/api/…` i 404. Zgłoszone
         * właścicielowi tamtej warstwy; do czasu poprawki offboarding używa własnego, poprawnego adresu.
         */
        getOffboardingIssuePdf: (issueId) =>
            api.get(`/document-issues/${issueId}/pdf`, { responseType: "blob" }),

        /**
         * Podgląd PRZED wydaniem — HTML ze znakiem wodnym „DOKUMENT NIEWYDANY”.
         * Odpowiedź jest tekstem, nie JSON-em: wchodzi wprost do ramki `DocumentHtmlPreview`.
         */
        previewOffboardingDocument: (caseId, code, data = {}) =>
            api.post(`/offboarding/cases/${caseId}/documents/${code}/preview-html`, data, { responseType: "text" }),

        /** Wydanie dokumentu sprawy: plik + hash w rejestrze, egzemplarz w części C akt. */
        issueOffboardingDocument: (caseId, code, data = {}) =>
            api.post(`/offboarding/cases/${caseId}/documents/${code}/issue`, data),

        /** „Dołącz do akt" — gdy przy wydaniu odznaczono zakładanie egzemplarza. Idempotentne. */
        attachOffboardingIssueToTeczka: (issueId) =>
            api.post(`/offboarding/documents/issues/${issueId}/attach-to-teczka`, {}),

        // ── Świadectwo pracy (z akt, także bez sprawy) ──────────────────

        /**
         * Podpowiedź świadectwa liczona NA SERWERZE z akt: okresy z umów, stanowiska, wymiar etatu,
         * wykorzystany urlop, spółki pracodawcy i ocena, czy świadectwo przysługuje.
         * `companyId`: pominięty = wszystkie okresy razem, „brak" = umowy bez wskazanej spółki.
         */
        getSwiadectwoPrefill: (employeeId, companyId) =>
            api.get(`/offboarding/employees/${employeeId}/swiadectwo/prefill`,
                { params: companyId === undefined || companyId === null || companyId === "" ? {} : { companyId } }),

        previewSwiadectwo: (employeeId, data = {}) =>
            api.post(`/offboarding/employees/${employeeId}/swiadectwo/preview-html`, data, { responseType: "text" }),

        /**
         * Wydanie świadectwa. Odpowiedź 409 `SWIADECTWO_NIE_PRZYSLUGUJE` to OSTRZEŻENIE, nie zakaz —
         * ponowienie z `potwierdzamBrakUmowyOPrace: true` wydaje dokument, a decyzja kadr zostaje
         * w notatce wydania i w dzienniku.
         */
        issueSwiadectwo: (employeeId, data = {}) =>
            api.post(`/offboarding/employees/${employeeId}/swiadectwo/issue`, data),
    };
}
